import { notifications } from '@mantine/notifications';
import {
  type QueryKey,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { ApiError, errorMessage } from '@/api/client';
import { playAttention } from '@/lib/attention';

export function notifySuccess(message: string) {
  notifications.show({ color: 'teal', message });
}

export function notifyError(error: unknown, title = 'Action impossible') {
  playAttention('erreur');
  notifications.show({ color: 'red', title, message: errorMessage(error) });
}

/**
 * Reporte les erreurs de validation du serveur (champ par champ) dans un formulaire
 * react-hook-form. Renvoie true si au moins un champ a été marqué.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
): boolean {
  if (!(error instanceof ApiError)) return false;
  const errors = (error.problem.errors ?? {}) as Record<string, unknown>;
  let applied = false;
  for (const [field, messages] of Object.entries(errors)) {
    const message = Array.isArray(messages) ? String(messages[0]) : String(messages);
    if (field === 'non_field_errors') continue;
    setError(field as Path<T>, { type: 'server', message });
    applied = true;
  }
  return applied;
}

/**
 * Mutation avec invalidation des requêtes concernées, notification de succès et
 * notification d'erreur (sauf si `onError` est fourni).
 */
export function useApiMutation<TData, TVariables>(
  fn: (variables: TVariables) => Promise<TData>,
  {
    invalidate = [],
    success,
    ...options
  }: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'> & {
    invalidate?: QueryKey[];
    success?: string | ((data: TData) => string);
  } = {},
) {
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVariables>({
    mutationFn: fn,
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all(invalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })));
      if (success) notifySuccess(typeof success === 'function' ? success(data) : success);
      await options.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      if (options.onError) options.onError(error, variables, onMutateResult, context);
      else notifyError(error);
    },
  });
}
